import { useNavigate, useSearchParams } from 'react-router-dom';
import { Text, Badge } from '@components/base/display';
import { Table } from '@components/base/data';
import { HStack, VStack } from '@components/base/layout';
import { Alert, Spinner } from '@components/base/feedback';
import { Select } from '@components/base/inputs';
import { Pagination } from '@components/base/navigation';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { erpRequestLogsService } from '../../api/erpRequestLogsService';
import type {
  ErpRequestLog,
  ErpRequestLogListParams,
} from '../../api/erpRequestLogsService';

type StatusFilter = '' | NonNullable<ErpRequestLogListParams['status']>;
type SortFilter = NonNullable<ErpRequestLogListParams['sort']>;

const DEFAULT_SORT: SortFilter = '-created_at';
const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [25, 50, 100];

const statusFilterOptions = [
  { value: '', label: 'All' },
  { value: 'success', label: 'Success' },
  { value: 'error', label: 'Error' },
];

const sortFilterOptions: { value: SortFilter; label: string }[] = [
  { value: '-created_at', label: 'Newest first' },
  { value: 'created_at', label: 'Oldest first' },
  { value: '-duration_ms', label: 'Slowest first' },
  { value: 'duration_ms', label: 'Fastest first' },
];

function methodBadgeVariant(
  method: string,
): 'default' | 'info' | 'warning' | 'success' | 'error' | 'primary' {
  switch (method.toUpperCase()) {
    case 'GET':
      return 'info';
    case 'POST':
      return 'warning';
    default:
      return 'default';
  }
}

function statusBadgeVariant(
  status: number | null,
): 'default' | 'info' | 'warning' | 'success' | 'error' | 'primary' {
  if (status === null) return 'default';
  if (status >= 200 && status < 300) return 'success';
  if (status >= 400) return 'error';
  return 'default';
}

function parseStatus(raw: string | null): StatusFilter {
  return raw === 'success' || raw === 'error' ? raw : '';
}

function parseSort(raw: string | null): SortFilter {
  const match = sortFilterOptions.find((opt) => opt.value === raw);
  return match ? match.value : DEFAULT_SORT;
}

function parsePage(raw: string | null): number {
  const page = Number(raw);
  return Number.isInteger(page) && page >= 1 ? page : 1;
}

function parsePageSize(raw: string | null): number {
  const size = Number(raw);
  return PAGE_SIZE_OPTIONS.includes(size) ? size : DEFAULT_PAGE_SIZE;
}

/* v8 ignore start -- single-select Selects never emit an array; this only narrows the union type */
function selectValue(val: string | string[]): string {
  return Array.isArray(val) ? (val[0] ?? '') : val;
}
/* v8 ignore stop */

function formatDuration(ms: number): string {
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
  return `${String(ms)}ms`;
}

export function ErpRequestLogsListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const statusFilter = parseStatus(searchParams.get('status'));
  const sortFilter = parseSort(searchParams.get('sort'));
  const page = parsePage(searchParams.get('page'));
  const perPage = parsePageSize(searchParams.get('per_page'));

  /**
   * Update URL params. Any change other than `page` itself resets to page 1.
   */
  const updateParams = (updates: Record<string, string>): void => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(updates)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    if (!('page' in updates)) next.delete('page');
    setSearchParams(next);
  };

  const handleStatusChange = (val: string | string[]): void => {
    updateParams({ status: selectValue(val) });
  };

  const handleSortChange = (val: string | string[]): void => {
    const sort = selectValue(val);
    updateParams({ sort: sort === DEFAULT_SORT ? '' : sort });
  };

  const handlePageChange = (nextPage: number): void => {
    updateParams({ page: nextPage > 1 ? String(nextPage) : '' });
  };

  const handlePageSizeChange = (size: number): void => {
    updateParams({ per_page: size === DEFAULT_PAGE_SIZE ? '' : String(size) });
  };

  const { data, isLoading, isPlaceholderData, error } = useQuery({
    queryKey: [
      'admin',
      'erp',
      'request-logs',
      { status: statusFilter, sort: sortFilter, page, per_page: perPage },
    ],
    queryFn: () =>
      erpRequestLogsService.list({
        status: statusFilter || undefined,
        sort: sortFilter,
        page,
        per_page: perPage,
      }),
    placeholderData: keepPreviousData,
  });

  const items: ErpRequestLog[] = data?.data ?? [];
  const meta = data?.meta;

  // ---- Loading state ----

  if (isLoading) {
    return (
      <VStack align="center" justify="center" gap={2} className="py-16">
        <Spinner size="lg" />
        <Text color="muted">Loading request logs...</Text>
      </VStack>
    );
  }

  // ---- Error state ----

  if (error) {
    return (
      <Alert variant="error" title="Failed to load request logs">
        {error instanceof Error
          ? error.message
          : 'An unexpected error occurred while loading request logs.'}
      </Alert>
    );
  }

  // ---- Main content ----

  return (
    <VStack gap={6}>
      {/* Page header */}
      <VStack gap={1}>
        <Text as="h1" size="2xl" weight="bold">
          ERP Request Logs
        </Text>
        <Text size="sm" color="muted">
          Monitor and inspect ERP API request history
        </Text>
      </VStack>

      {/* Filters */}
      <HStack gap={4} wrap>
        <Select
          label="Status"
          options={statusFilterOptions}
          value={statusFilter}
          onChange={handleStatusChange}
          helperText="Error = HTTP or transport failures (rows with an error code)"
          size="sm"
        />
        <Select
          label="Sort"
          options={sortFilterOptions}
          value={sortFilter}
          onChange={handleSortChange}
          size="sm"
        />
      </HStack>

      {/* Table or empty state */}
      {items.length === 0 ? (
        <Alert variant="info" title="No request logs found">
          No request logs match the current filters.
        </Alert>
      ) : (
        <Table className={isPlaceholderData ? 'opacity-60' : undefined}>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Endpoint</Table.HeaderCell>
              <Table.HeaderCell align="center">Method</Table.HeaderCell>
              <Table.HeaderCell align="center">Status</Table.HeaderCell>
              <Table.HeaderCell align="right">Duration</Table.HeaderCell>
              <Table.HeaderCell align="center">Retries</Table.HeaderCell>
              <Table.HeaderCell>Date</Table.HeaderCell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {items.map((log) => (
              <Table.Row
                key={log.id}
                onClick={() =>
                  navigate(`/admin/erp/request-logs/${log.id}`)
                }
              >
                <Table.Cell>
                  <VStack gap={0}>
                    <Text weight="medium">
                      {log.erp_endpoint?.name ?? log.erp_endpoint?.slug ?? '—'}
                    </Text>
                    <Text size="sm" color="muted">
                      {log.erp_registry?.name ?? '—'}
                    </Text>
                  </VStack>
                </Table.Cell>
                <Table.Cell align="center">
                  <Badge
                    variant={methodBadgeVariant(log.request_method)}
                    size="sm"
                  >
                    {log.request_method.toUpperCase()}
                  </Badge>
                </Table.Cell>
                <Table.Cell align="center">
                  <VStack gap={0}>
                    <Badge
                      variant={statusBadgeVariant(log.response_status)}
                      size="sm"
                    >
                      {log.response_status ?? '—'}
                    </Badge>
                    {log.error_code && (
                      <Text size="sm" color="error">
                        {log.error_code}
                      </Text>
                    )}
                  </VStack>
                </Table.Cell>
                <Table.Cell align="right">
                  <Text size="sm" color="muted">
                    {formatDuration(log.duration_ms)}
                  </Text>
                </Table.Cell>
                <Table.Cell align="center">
                  <Text size="sm" color="muted">
                    {log.retry_count > 0 ? log.retry_count : '—'}
                  </Text>
                </Table.Cell>
                <Table.Cell>
                  <Text size="sm" color="muted">
                    {new Date(log.created_at).toLocaleString()}
                  </Text>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {/* Pagination (hidden on the empty state) */}
      {items.length > 0 && meta && (
        <Pagination
          page={meta.current_page}
          totalPages={meta.last_page}
          totalItems={meta.total}
          pageSize={meta.per_page}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          showFirstLast
        />
      )}
    </VStack>
  );
}
